import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import type { DataTableValueArray } from "primereact/datatable";
import { FilterMatchMode } from "primereact/api";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import {
  DEFAULT_ROW_COUNT,
  ROWS_PER_PAGE_OPTIONS,
} from "sj-ui-toolkit/src/constants/tableRowsCounts";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import type { AssignmentPolicyData } from "@/api/assignments";
import { skeletonRows, withSkeleton } from "@/helpers/tableBody";
import { textFilterElement, triStateBooleanFilterElement } from "@/helpers/tableFilters";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PlaProgressData {
  total: number;
  complete: number;
  inProgress: number;
  notStarted: number;
}

interface AssignmentPolicyRow extends AssignmentPolicyData {
  completePercentage: number;
  inProgressPercentage: number;
  notStartedPercentage: number;
  abandonedPercentage: number;
  audiencesText: string;
  // Pre-formatted fields used only for CSV export (set via exportRows)
  _audiencesCsv?: string;
  _dueDateCsv?: string;
}

interface AssignmentsTableProps {
  assignmentPolicies: AssignmentPolicyData[];
  isLoading: boolean;
  viewOnly: boolean;
  processingAssignmentIds?: Set<string>;
  aspenAdaptEnabled?: boolean;
  plaProgress?: PlaProgressData | null;
  plaLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EMPTY_ROW_TEXT = "There are no available assignments...";
const NO_DUE_DATE = "0001-01-01T00:00:00Z";
const STATE_KEY = "adminAssignmentsTable";
const PLA_ROW_ID = "personalized-lesson-assignment";
const PERSONALIZED_LESSON_ASSIGNMENT_PATH =
  AdminAppRoutes.routes.assignments.routes.personalizedLessonAssignment.route;

const PERSONALIZED_LESSON_ASSIGNMENT_ROW = {
  id: PLA_ROW_ID,
  name: "Personalized Lesson Assignment",
  personalizedLessonAssignment: true,
};

const EMPTY_FILTERS = {
  global: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  name: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  audiencesText: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  activated: { value: null as boolean | null, matchMode: FilterMatchMode.EQUALS },
};

// ---------------------------------------------------------------------------
// Helpers — column body templates (no runtime deps)
// ---------------------------------------------------------------------------

const audiencesBody = withSkeleton<AssignmentPolicyRow>(
  row =>
    (row as AssignmentPolicyRow & { personalizedLessonAssignment?: boolean })
      .personalizedLessonAssignment
      ? "-"
      : (row.audiences?.map(a => a.name).join(", ") ?? ""),
  "10rem"
);

const audiencesFilterElement = textFilterElement("Search...");

const activeBody = withSkeleton<AssignmentPolicyRow>(row => {
  if (
    (row as AssignmentPolicyRow & { personalizedLessonAssignment?: boolean })
      .personalizedLessonAssignment
  )
    return <span>-</span>;
  return (
    <i
      className={`pi ${
        row.activated ? "pi-check-circle" : `pi-times-circle ${themeClasses.iconError}`
      }`}
    />
  );
}, "2rem");

const dueDateBody = withSkeleton<AssignmentPolicyRow>(
  row =>
    (row as AssignmentPolicyRow & { personalizedLessonAssignment?: boolean })
      .personalizedLessonAssignment
      ? "-"
      : row.dueDateAt && row.dueDateAt !== NO_DUE_DATE
        ? humanDateWithoutTime(row.dueDateAt)
        : "-",
  "6rem"
);

const makePercentageBody = (field: keyof AssignmentPolicyRow) =>
  withSkeleton<AssignmentPolicyRow>(row => {
    const isPla = (row as AssignmentPolicyRow & { personalizedLessonAssignment?: boolean })
      .personalizedLessonAssignment;
    // PLA has no "Paused" state
    if (isPla && field === "abandonedPercentage") return <span>-</span>;
    return row.progress?.["totalAssigned"] === "0" ? "-" : `${(row[field] as number).toFixed(0)}%`;
  }, "4rem");

const completeBody = makePercentageBody("completePercentage");
const inProgressBody = makePercentageBody("inProgressPercentage");
const notStartedBody = makePercentageBody("notStartedPercentage");
const abandonedBody = makePercentageBody("abandonedPercentage");

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AssignmentsTable = ({
  assignmentPolicies,
  isLoading,
  viewOnly,
  processingAssignmentIds = new Set(),
  aspenAdaptEnabled = false,
  plaProgress = null,
  plaLoading = false,
}: AssignmentsTableProps) => {
  const dt = useRef<DataTable<DataTableValueArray>>(null);
  const navigate = useNavigate();

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  // Restore persisted search value on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STATE_KEY);
      if (stored) {
        const data: { filters?: { global?: { value?: string } } } = JSON.parse(stored);
        const savedValue = data.filters?.global?.value;
        if (savedValue) setGlobalFilterValue(savedValue);
      }
    } catch {
      // ignore corrupt storage
    }
  }, []);

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, global: { ...prev.global, value } }));
    setGlobalFilterValue(value);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setGlobalFilterValue("");
  };

  const exportCSV = () => {
    dt.current?.exportCSV({ selectionOnly: false });
  };

  // Add computed percentage fields for sortable columns
  const calculatedPolicies: AssignmentPolicyRow[] = useMemo(
    () =>
      assignmentPolicies.map(policy => {
        const progress = policy.progress ?? {};
        const total = Number(progress["totalAssigned"] ?? 0);
        return {
          ...policy,
          audiencesText: policy.audiences?.map(a => a.name).join(", ") ?? "",
          completePercentage: total ? (((progress["complete"] as number) ?? 0) / total) * 100 : 0,
          inProgressPercentage: total
            ? (((progress["inProgress"] as number) ?? 0) / total) * 100
            : 0,
          notStartedPercentage: total
            ? (((progress["notStarted"] as number) ?? 0) / total) * 100
            : 0,
          abandonedPercentage: total ? (((progress["abandoned"] as number) ?? 0) / total) * 100 : 0,
        };
      }),
    [assignmentPolicies]
  );

  // Meta PLA row — aggregate stats across all approved PLAs
  const plaTotal = plaProgress?.total ?? 0;
  const plaRow = useMemo<AssignmentPolicyRow>(
    () => ({
      ...(PERSONALIZED_LESSON_ASSIGNMENT_ROW as unknown as AssignmentPolicyData),
      audiencesText: "-",
      progress: { totalAssigned: String(plaTotal) },
      completePercentage: plaTotal ? ((plaProgress?.complete ?? 0) / plaTotal) * 100 : 0,
      inProgressPercentage: plaTotal ? ((plaProgress?.inProgress ?? 0) / plaTotal) * 100 : 0,
      notStartedPercentage: plaTotal ? ((plaProgress?.notStarted ?? 0) / plaTotal) * 100 : 0,
      abandonedPercentage: 0,
    }),
    [plaProgress, plaTotal]
  );

  const tableData = useMemo(
    () => (aspenAdaptEnabled ? [plaRow, ...calculatedPolicies] : calculatedPolicies),
    [aspenAdaptEnabled, plaRow, calculatedPolicies]
  );

  // Read persisted row count once on mount so skeleton and paginator agree
  const storedRows = useMemo(() => {
    try {
      const stored = localStorage.getItem(STATE_KEY);
      if (stored) {
        const parsed: { rows?: number } = JSON.parse(stored);
        if (parsed.rows) return parsed.rows;
      }
    } catch {
      // ignore
    }
    return DEFAULT_ROW_COUNT;
  }, []);

  // Skeleton items — null-based, count matches the DataTable's initial rows prop
  const skeletonItems = useMemo(() => skeletonRows<AssignmentPolicyRow>(storedRows), [storedRows]);

  // ---------------------------------------------------------------------------
  // Column body templates — runtime deps only
  // ---------------------------------------------------------------------------

  const nameBody = useMemo(
    () =>
      withSkeleton<AssignmentPolicyRow>(row => {
        const isPla = (row as AssignmentPolicyRow & { personalizedLessonAssignment?: boolean })
          .personalizedLessonAssignment;
        if (isPla) {
          return (
            <Button
              type="button"
              link
              className={`${themeClasses.textPrimaryDarker} cursor-pointer p-0 text-left`}
              icon="pi pi-user"
              label={row.name}
              onClick={() => navigate(PERSONALIZED_LESSON_ASSIGNMENT_PATH)}
            />
          );
        }
        return (
          <Button
            type="button"
            link
            className={`${themeClasses.textPrimaryDarker} cursor-pointer p-0 text-left`}
            label={row.name}
            onClick={() =>
              navigate(
                AdminAppRoutes.routes.assignments.routes.id.route.replace(":id", row.id ?? "")
              )
            }
          />
        );
      }, "12rem"),
    [navigate]
  );

  const totalEnrolledBody = useMemo(
    () =>
      withSkeleton<AssignmentPolicyRow>(row => {
        const isPla = (row as AssignmentPolicyRow & { personalizedLessonAssignment?: boolean })
          .personalizedLessonAssignment;
        if (isPla) {
          if (plaLoading) return <i className="pi pi-spin pi-spinner-dotted" />;
          return <>{row.progress?.["totalAssigned"]}</>;
        }
        if (processingAssignmentIds.has(row.id ?? "")) {
          return (
            <div className="flex items-center gap-2">
              <i className="pi pi-spin pi-spinner-dotted" />
              <span className="text-secondary">Processing</span>
            </div>
          );
        }
        return <>{row.progress?.["totalAssigned"]}</>;
      }, "4rem"),
    [processingAssignmentIds, plaLoading]
  );

  // ---------------------------------------------------------------------------
  // Header
  // ---------------------------------------------------------------------------

  const renderHeader = () => (
    <div className="grid grid-cols-2 mb-4">
      <div className="flex flex-start gap-4">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            id="admin-assignment-search-input"
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Keyword Search"
            autoComplete="off"
            data-testid="admin-assignment-search-input"
          />
        </IconField>
        <Button
          id="admin-assignment-clear-filter"
          label="Clear"
          icon="pi pi-filter-slash"
          size="small"
          severity="secondary"
          data-testid="admin-assignment-clear-filter-button"
          className={globalFilterValue ? "" : "hidden"}
          onClick={clearFilters}
        />
      </div>
      <div className="flex flex-row-reverse gap-4">
        <Button
          label="Export data"
          icon="pi pi-download"
          iconPos="left"
          disabled={isLoading}
          size="small"
          onClick={exportCSV}
          tooltip="Export data as CSV"
          tooltipOptions={{ position: "left" }}
        />
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h1>Assignments</h1>
        <Button
          id="admin-create-new-assignment"
          type="button"
          label="Create New"
          icon="pi pi-plus"
          disabled={isLoading || viewOnly}
          data-testid="admin-create-new-assignment-button"
          onClick={() => navigate(AdminAppRoutes.routes.assignments.routes.new.route)}
        />
      </div>
      <Card data-testid="assignments-card" className={themeClasses.bgElevation1}>
        {renderHeader()}
        <DataTable
          ref={dt}
          id="assignmentsTable"
          aria-label="Assignments list"
          data-testid="assignmentsTable"
          value={(isLoading ? skeletonItems : tableData) as unknown as DataTableValueArray}
          stateKey={STATE_KEY}
          stateStorage="local"
          paginator
          filters={isLoading ? undefined : filters}
          onFilter={e => setFilters({ ...EMPTY_FILTERS, ...(e.filters as typeof EMPTY_FILTERS) })}
          filterDisplay="menu"
          size="normal"
          globalFilterFields={isLoading ? [] : ["name"]}
          rows={storedRows}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          removableSort
          onStateSave={(state: Record<string, unknown>) => {
            const cleaned = { ...state };
            delete cleaned.sortField;
            delete cleaned.sortOrder;
            localStorage.setItem(STATE_KEY, JSON.stringify(cleaned));
          }}
          emptyMessage={EMPTY_ROW_TEXT}
          tableClassName="dt-responsive-text"
          exportFunction={({ data, field, rowData }) => {
            if (field === "dueDateAt") {
              const row = rowData as AssignmentPolicyRow;
              return row.dueDateAt && row.dueDateAt !== NO_DUE_DATE
                ? humanDateWithoutTime(row.dueDateAt)
                : "-";
            }
            return String(data ?? "")
              .replace(/"/g, '""')
              .replace(/\n/g, "\u2028");
          }}
        >
          <Column
            field="name"
            header="Assignment"
            sortable
            filter
            showFilterMatchModes={false}
            filterElement={textFilterElement("Search...")}
            body={nameBody}
            style={{ width: "350px" }}
          />
          <Column
            field="audiencesText"
            header="Audiences"
            sortable
            filter
            showFilterMatchModes={false}
            filterElement={audiencesFilterElement}
            body={audiencesBody}
            style={{ width: "300px" }}
          />
          <Column
            field="activated"
            header="Active"
            sortable
            filter
            filterElement={triStateBooleanFilterElement}
            showFilterMatchModes={false}
            body={activeBody}
            dataType="boolean"
            style={{ width: "150px" }}
          />
          <Column
            field="dueDateAt"
            header="Due Date"
            sortable
            body={dueDateBody}
            style={{ width: "200px" }}
          />
          <Column
            field="progress.totalAssigned"
            header="Total Enrolled"
            sortable
            body={totalEnrolledBody}
          />
          <Column field="completePercentage" header="Complete" sortable body={completeBody} />
          <Column
            field="inProgressPercentage"
            header="In Progress"
            sortable
            body={inProgressBody}
          />
          <Column
            field="notStartedPercentage"
            header="Not Started"
            sortable
            body={notStartedBody}
          />
          <Column field="abandonedPercentage" header="Paused" sortable body={abandonedBody} />
        </DataTable>
      </Card>
    </>
  );
};
